import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { AoiShape } from "../models/aois/AoiShape"
import { Project, ProjectData } from "../models/projects/Project"
import { ProjectDetails } from "../models/projects/ProjectDetails"
import { ProjectInfoShort } from "../models/projects/ProjectInfoShort"
import { UpdateProject } from "../models/projects/UpdateProject"
import { API, NexusSuccessResponse } from "./API"
import { QueryKeys } from "./QueryConfig"

const projectQueryKey = (projectNumber?: string, fullData?: boolean) => {
    const key: string[] = [QueryKeys.FIND_PROJECT]
    if (projectNumber != null) {
        key.push(projectNumber)
    }
    if (fullData === true) {
        key.push("fullData")
    }
    return key
}

export const useFetchProject = (projectNumber: string) => {
    return useQuery({
        queryKey: projectQueryKey(projectNumber, false),
        queryFn: async (): Promise<Project> => {
            const result: NexusSuccessResponse<Project> = await API.get(
                `/project/${projectNumber}`,
                `Could not load project title for project number ${projectNumber}.`,
            )
            return result.data
        },
        refetchOnMount: false,
        refetchOnWindowFocus: false,
    })
}

export const useFetchProjectFullData = (projectNumber: string) => {
    return useQuery({
        // True represents "full data"
        queryKey: projectQueryKey(projectNumber, true),
        queryFn: async (): Promise<ProjectData> => {
            const result: NexusSuccessResponse<ProjectData> = await API.get(
                `/project-data/${projectNumber}`,
                `Could not fetch project data for project number: ${projectNumber}.`,
            )
            return result.data
        },
        refetchOnMount: false,
        refetchOnWindowFocus: false,
    })
}

export const useFetchNonAcquisitionProject = (projectNumber?: string) => {
    return useQuery({
        queryKey: [QueryKeys.FIND_NON_ACQUISITION_PROJECT, projectNumber],
        queryFn: async (): Promise<Project> => {
            // TODO Sucks that the /project/:projectNumber endpoint returns variable data structures depending on
            //  project status. Fix that in concert with the backend
            const result: NexusSuccessResponse<Project> = await API.get(
                `/project/${projectNumber}`,
                `Could not fetch project data for non-acquisition project number: ${projectNumber}.`,
            )
            return result.data
        },
        refetchOnMount: false,
        refetchOnWindowFocus: false,
        enabled: projectNumber != null,
    })
}

export const useFetchProjectAoi = (id?: number) => {
    return useQuery({
        queryKey: [QueryKeys.FIND_PROJECT_AOI, id],
        queryFn: async (): Promise<AoiShape> => {
            const result: NexusSuccessResponse<AoiShape> = await API.get(
                `/aoi/${id}`,
                `Could not fetch AOI with id: ${id}.`,
            )
            return result.data
        },
        gcTime: 0,
        refetchOnMount: false,
        refetchOnWindowFocus: false,
        enabled: id != null,
    })
}

export const useDeleteProjectAoi = () => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (aoiId: number): Promise<void> => {
            await API.delete(`/aoi/${aoiId}`, `Error deleting aoi with id ${aoiId}.`)
            await queryClient.invalidateQueries({ queryKey: projectQueryKey() })
        },
    })
}

export interface AoiFileParseResponse {
    aoi: string
    area_m2: number
    area_contracted_m2: number
}

export const useParseAoiFile = () => {
    return useMutation({
        mutationFn: async ({
            file,
            doPolygonRepairs,
        }: {
            file: File
            doPolygonRepairs: boolean
        }): Promise<AoiFileParseResponse> => {
            try {
                const formData = new FormData()
                formData.append("file", file)

                const response: NexusSuccessResponse<AoiFileParseResponse> = await API.uploadFile(
                    "/upload/aoi",
                    formData,
                    { repair_polygons: doPolygonRepairs },
                    "Unable to parse AOI file.",
                )
                return response.data
            } catch (error) {
                if (error.message === "413") {
                    throw new Error("File too large (50MB max).")
                }
                throw error
            }
        },
    })
}

export const useUpdateProject = () => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async ({
            selectedProjectData,
            selectedAOIData,
        }: {
            selectedProjectData: Project
            selectedAOIData?: AoiShape
        }): Promise<void> => {
            const { project_number, project_detail } = selectedProjectData

            if (project_number == null || project_detail == null) {
                throw new Error("Project Number and Project Detail must both be non-null to update a project.")
            }

            const projectToUpdate: ProjectDetails = {
                ...project_detail,
            }

            projectToUpdate.contract_link = projectToUpdate.contract_link?.trim() ?? null
            if (projectToUpdate.contract_link?.length === 0) {
                projectToUpdate.contract_link = null
            }

            const updatedData: UpdateProject = {
                project_detail: projectToUpdate,
            }
            if (selectedAOIData) {
                updatedData.aoi = selectedAOIData
            }

            await API.put(
                `/project-data/${project_number}`,
                updatedData,
                `Error updating project data for project number ${project_number}.`,
            )
            await queryClient.invalidateQueries({ queryKey: projectQueryKey(project_number) })
        },
    })
}

export const useFindAllProjects = () => {
    return useQuery({
        queryKey: projectQueryKey(),
        queryFn: async (): Promise<ProjectInfoShort[]> => {
            const result: NexusSuccessResponse<ProjectInfoShort[]> = await API.get(
                "/projects/all",
                "Error retrieving project list.",
            )
            return result.data
        },
        refetchOnMount: false,
        refetchOnWindowFocus: false,
    })
}

export const useFindAllNonAcquisitionProjects = () => {
    return useQuery({
        queryKey: [QueryKeys.FIND_NON_ACQUISITION_PROJECT],
        queryFn: async (): Promise<ProjectInfoShort[]> => {
            const result: NexusSuccessResponse<ProjectInfoShort[]> = await API.get(
                "/projects/non-acquisition",
                "Error retrieving non-acquisition project list.",
            )
            return result.data
        },
        refetchOnMount: false,
        refetchOnWindowFocus: false,
    })
}

export const useInitializeAcquisitionProject = () => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (projectNumber: string): Promise<void> => {
            await API.post(
                "/project-details",
                {
                    project_number: projectNumber,
                },
                `Error initializing acquisition project with number: ${projectNumber}.`,
            )
            await queryClient.invalidateQueries({ queryKey: [QueryKeys.FIND_NON_ACQUISITION_PROJECT] })
            await queryClient.invalidateQueries({ queryKey: projectQueryKey() })
        },
    })
}
